import { cx } from '@emotion/css';
import { AddCircle, ArrowForwardRounded } from '@mui/icons-material';
import { Avatar, Button } from '@sortlist-frontend/design-system/legacy';
import { Trans, useTranslation } from '@sortlist-frontend/translation/ssr';
import { UseComboboxPropGetters, UseComboboxState } from 'downshift';
import { Fragment } from 'react';

import { agencyClaimConfig } from '_features/agency/claim/agency-claim.config';

import { AgenciesAutocompletePayload } from '../../../../../core/repos/agencies-autocomplete.repo';

type Props = {
  items: AgenciesAutocompletePayload[];
  getItemProps: UseComboboxPropGetters<AgenciesAutocompletePayload>['getItemProps'];
  highlightedIndex: UseComboboxState<AgenciesAutocompletePayload>['highlightedIndex'];
  selectedItem: AgenciesAutocompletePayload | null;
};

export const AgencyAutocompleteSuggestions = (props: Props) => {
  const { items, getItemProps, highlightedIndex } = props;
  const { t } = useTranslation(agencyClaimConfig.i18nNamespaces);
  return (
    <Fragment>
      {items.map((item, index) => {
        const listClassName = cx('layout-row layout-align-start-center', {
          'border-top border-secondary-300': index !== 0,
          'bg-primary-100 text-primary-700': highlightedIndex === index,
        });
        const btnLabel: Record<typeof item.status, string> = {
          CLAIMED: t('agency:claim.landing.claimed'),
          UNCLAIMED: t('agency:claim.landing.unclaimed'),
          NEW_RECORD: t('agency:claim.landing.newRecord'),
        };
        return (
          <li
            key={item.slug}
            {...getItemProps({
              index,
              item: item,
              className: listClassName,
            })}>
            {item.status === 'NEW_RECORD' && <AddCircle className="m-4" style={{ fontSize: 16 }} />}
            {item.status !== 'NEW_RECORD' && (
              <Avatar className="m-4" shape="circle" size="sm" fit="contain" src={item.logo} />
            )}
            <div className="flex-1 text-left">
              {item.status === 'NEW_RECORD' && (
                <p className="text-truncate">
                  <Trans i18nKey={'agency:claim.landing.create'} values={{ agencyName: item.name }}>
                    Create <b>{'{{agencyName}}'}</b>'s profile
                  </Trans>
                </p>
              )}
              {item.status !== 'NEW_RECORD' && <p className="small bold text-truncate">{item.name}</p>}
              <small className="text-truncate small">{item.website}</small>
            </div>
            <div className="flex-35 layout-row p-4">
              <Button
                label={btnLabel[item.status]}
                size="xs"
                className="flex-1 layout-row"
                buttonVariant={highlightedIndex === index ? 'raised' : 'light'}
                buttonStyle="primary"
                iconRight={<ArrowForwardRounded style={{ fontSize: 14 }} />}
              />
            </div>
          </li>
        );
      })}
    </Fragment>
  );
};
